import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  Grid,
  Table,
} from "semantic-ui-react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";

import {
  AccordionTitleView,
  CommonTable,
  CustomButton,
  DropDown,
} from "../../../../../components";
import { InputNumber } from "../../../../../components/InputText/InputNumber";
import PlusIcon from "../../../../../assets/commonIcon/PlusIcon";
import { useGetFertilizerData } from "../../../../../api/static-data";
import { errorView } from "../../../../../helpers/ErrorHandler";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { CULTIVATION_TYPE, FertilizerTableHeader } from "../../../../../config/constants";

import "../styles/CultivarForm.scss";
import { validateCultivationNumber } from "../../../../../utils/utils";

const Fertilizer = forwardRef((
  {
    currentFormData,
    currentDataType,
    currentOriginalFormData
  }: any,
  ref
) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [enableUpdateButton, setEnableUpdateButton] = useState<any>(false);
  const [fertilizer, setFertilizer] = useState<any>("");
  const [fertilizerDropdownList, setFertilizerDropdownList] = useState<any>([]);
  const [fertilizerTableList, setFertilizerTableList] = useState<any>([]);
  const [fertilizerOriginalTableList, setFertilizerOriginalTableList] = useState<any>([]);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteValue, setDeleteValue] = useState<any>({
    item: "",
    type: "",
  });

  const {
    data: fertilizerListData,
    isSuccess: isFertilizerListDataSuccess,
  } = useGetFertilizerData();

  const fertilizerTableListData = useCallback(() => {
    const currentFertilizerData = currentFormData?.cultivationField?.fertilizerList?.map((item: any) => {
      const fertilizer = fertilizerListData?.find((f: any) => {
        return parseInt(f.id) === parseInt(item.id);
      });
      return {
        ...item,
        fertilizer_name: fertilizer?.fertilizer_name ?? "",
        id: parseInt(item.id)
      };
    });
    setFertilizerTableList(currentFertilizerData)
  }, [currentFormData, fertilizerListData]);

  const fertilizerOriginalTableListData = useCallback(() => {
    const currentFertilizerData = currentOriginalFormData?.cultivationField?.fertilizerList?.map((item: any) => {
      const fertilizer = fertilizerListData?.find((f: any) => {
        return parseInt(f.id) === parseInt(item.id);
      });
      return {
        ...item,
        fertilizer_name: fertilizer?.fertilizer_name ?? "",
        id: parseInt(item.id)
      };
    });
    setFertilizerOriginalTableList(currentFertilizerData)
  }, [currentFormData, fertilizerListData]);

  useEffect(() => {
    fertilizerTableListData()
    fertilizerOriginalTableListData()
  }, [currentFormData, fertilizerListData])

  const values = useMemo(() => {
    setFertilizerDropdownList(fertilizerListData)
    return {
      fertilizerQuantity: "",
    }
  }, [currentFormData, fertilizerListData, isFertilizerListDataSuccess])

  const {
    formState: { errors },
    control,
    setValue,
    handleSubmit
  } = useForm({ defaultValues: values, values: values });

  useImperativeHandle(ref, () => ({
    getFretilizerData: () => {
      return fertilizerTableList
    }
  }));

  const AddFertilizer = handleSubmit((values: any) => {
    let updatedFertilizerDropdownList = [...fertilizerDropdownList];
    let updatedFertilizerTableList = [...fertilizerTableList];
    if (
      updatedFertilizerDropdownList.length === 0 ||
      !fertilizer
    ) {
      errorView("Select fertilizer");
      return;
    }

    const doesFertilizerExists = updatedFertilizerTableList.find(
      (item) => parseInt(item.id) === parseInt(fertilizer)
    );
    if (doesFertilizerExists) {
      updatedFertilizerTableList = updatedFertilizerTableList.map((p) => {
        if (parseInt(p.id) === parseInt(fertilizer)) {
          p.fertilizerQuantity = values.fertilizerQuantity;
        }
        return p;
      });
    } else {
      const fertilizerFromList = updatedFertilizerDropdownList.find(
        (item) => item.id === fertilizer
      );
      if (fertilizerFromList) {
        updatedFertilizerTableList.push({
          ...fertilizerFromList,
          fertilizerQuantity: values.fertilizerQuantity,
        });
        updatedFertilizerDropdownList = updatedFertilizerDropdownList.filter(
          (item) => {
            return item.id !== fertilizer;
          }
        );
      }
    }
    setFertilizerTableList(updatedFertilizerTableList);
    setFertilizerDropdownList(updatedFertilizerDropdownList);
    setValue("fertilizerQuantity", "");
    setFertilizer("");
  })

  const deleteFertilizer = useCallback(() => {
    setFertilizerTableList((prevList: any[]) =>
      prevList.filter((_, index: number) => index !== deleteValue.index)
    );
    setVisibleDeleteModal(false);
    setDeleteValue({});
  }, [deleteValue])

  const onEditFertilizer = (item: any) => {
    setValue("fertilizerQuantity", item?.fertilizerQuantity ?? "");
    setFertilizerDropdownList((fertilizerDropdownList: any) => [
      ...fertilizerDropdownList,
      item,
    ]);
    setFertilizer(item.id.toString());
  }

  const editFertilizer = (item: any) => {
    onEditFertilizer(item);
    setEnableUpdateButton(true);
  }

  const deleteFertilizerData = (item: any, index: any) => {
    setVisibleDeleteModal(true);
    setDeleteValue({
      type: "Fertilizer",
      item: item ?? "",
      index: index,
    });
  }

  return currentDataType === CULTIVATION_TYPE.Supplier ? (
    <>
      <AccordionTitleView
        accordionEnabled={false}
        title={"Fertilizer"}
        plusIcon={false}
      />
      <div>
        <Grid>
          <Grid.Row divided>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              {fertilizerOriginalTableList?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={FertilizerTableHeader?.filter(
                      (e: any) => e.name !== ""
                    )}
                    className="pesticideTable"
                    isSimulateView
                  >
                    {fertilizerOriginalTableList?.map((item: any) => {
                      return (
                        <Table.Row key={`fertilizer_${item.key}`}>
                          <Table.Cell>
                            <p>{item?.fertilizer_name ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.fertilizerQuantity ?? ""}</p>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <Grid>
                <Grid.Column computer={7} tablet={7} mobile={16}>
                  <DropDown
                    labelName={"Fertilizer Name"}
                    placeholder="Select Fertilizer Name"
                    defaultValue={fertilizer}
                    currentData={fertilizerDropdownList}
                    handleChangeState={(e: any, { value }: any) => {
                      setFertilizer(value);
                    }}
                    search
                    key={"fertilizer_name"}
                  />
                </Grid.Column>
                <Grid.Column computer={7} tablet={7} mobile={16}>
                  <InputNumber
                    transform={{
                      input: (value: any) =>
                        isNaN(value) || value === 0 ? "" : value.toString(),
                      output: (e: any) => {
                        return e;
                      },
                    }}
                    control={control}
                    labelName={"kg/ha"}
                    placeholder="kg/ha"
                    name="fertilizerQuantity"
                    errors={errors.fertilizerQuantity}
                    required
                    errorMessage="Quantity is required"
                    otherValidation={{
                      validate: {
                        valueCheck: (v: any) => validateCultivationNumber(v)
                      }
                    }}
                  />
                </Grid.Column>
                <Grid.Column computer={2} tablet={2} mobile={16}>
                  <CustomButton
                    theme="green"
                    customColumnStyle={"addUnitSize"}
                    icon
                    iconChild={<PlusIcon />}
                    customBase="productUnitAddButton"
                    onClick={() => {
                      AddFertilizer();
                      setEnableUpdateButton(false);
                    }}
                  />
                </Grid.Column>
              </Grid>
              {fertilizerTableList?.length > 0 && (
                <Grid.Column
                  computer={16}
                  tablet={16}
                  mobile={16}
                  className="pesticideTable"
                >
                  <CommonTable
                    tableHeaderData={FertilizerTableHeader}
                    className="pesticideTable"
                  >
                    {fertilizerTableList.map((item: any, index: any) => {
                      return (
                        <Table.Row key={`fertilizer_${item.key}`}>
                          <Table.Cell>
                            <p>{item?.fertilizer_name ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <p>{item?.fertilizerQuantity ?? ""}</p>
                          </Table.Cell>
                          <Table.Cell>
                            <Grid>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdModeEditOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => editFertilizer(item)}
                                />
                              </Grid.Column>
                              <Grid.Column computer={8} tablet={16} mobile={16}>
                                <MdDeleteOutline
                                  cursor={"pointer"}
                                  size={24}
                                  color="var(--tableEditIcon)"
                                  onClick={() => deleteFertilizerData(item, index)
                                  }
                                />
                              </Grid.Column>
                            </Grid>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </CommonTable>
                </Grid.Column>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteValue({});
        }}
        approve={deleteFertilizer}
        title={`Delete ${deleteValue.type}`}
        subTitle={`Are you sure you want to delete Fertilizer data?`}
      />
    </>
  ) : null;
})

export default Fertilizer;
